import * as React from 'react'

import {
  PageMeta,
  HowToUse,
  BehindScenes,
  Callout,
  Flow,
  KVTable,
  Tag,
} from '@/components/docs/primitives'
import { H3, P, UL } from '@/components/docs/prose'
import { brand } from '@/brand/brand'

export function DocProducts() {
  return (
    <>
      <PageMeta
        title="Products"
        summary={`Your product library in ${brand.productName}. Every product you sell — Novera Body Oil, Novera Radiance Serum, the Novera Hair Revival Kit and the rest — gets described once, linked to its reference images, and then reused everywhere in the app.`}
      />

      <HowToUse>
        <H3>What lives here</H3>
        <P>
          Every SKU lives in this library with its name, description, benefits, ingredients,
          price, and linked product photography. This is the source of truth the AI pulls from when
          you write an ad — so if the Novera Body Oil's description says "sumptuous dry oil that
          absorbs in seconds", that exact language informs every hook, CTA, and scene prompt you
          generate for it. You enter it once; you never type it again.
        </P>

        <H3>Two ways to add a product</H3>
        <P>
          <strong>Scrape from your website</strong> — paste a product URL (for example, the Novera
          Radiance Serum page) and click Scrape. The system reads the page and extracts the name,
          description, benefits, ingredients, and price automatically. It also downloads the hero
          image and places it in your Asset Library, already linked to the product.
        </P>
        <Callout variant="tip">
          Scraping works best on actual product detail pages — the URL should be for a single
          product, not a collection or category page. Category pages, collection pages, and
          landing pages give worse results because there is no single clear product name, hero
          image, or structured copy to pull from.
        </Callout>
        <P>
          <strong>Add manually</strong> — fill in the fields yourself. Useful for new launches
          that are not yet live on your website, or for products where the page copy needs
          editorial polish before it feeds into ad generation.
        </P>

        <H3>Editing a product</H3>
        <P>
          Open any product and edit any field — update the Radiance Serum benefits list after a
          reformulation, adjust the Hair Revival Kit's brand voice after a campaign refresh, add
          new usage tips for the Shower Oil. Click Save. Changes feed into all future ad
          generations immediately. Completed ads already in your Jobs history are not
          re-rendered.
        </P>

        <H3>Linking images with roles</H3>
        <P>
          Linking product photography with roles tells the AI which angle each image represents,
          so it can pick the right combination automatically when you generate a composite or
          reference-to-video ad. The three roles that matter most are:
        </P>
        <UL>
          <li>
            <strong>hero_image</strong> — the main front-on shot, clean background, full label
            in frame. This is the default for any ad that references the product. For Novera Body
            Oil, this is the amber bottle straight-on with your wordmark clearly visible.
          </li>
          <li>
            <strong>three_quarter</strong> — a 3/4 angle that shows the product's depth and
            shape. The AI uses this to keep proportions accurate and avoid the flattened,
            two-dimensional look you sometimes get from a single reference angle.
          </li>
          <li>
            <strong>label_detail</strong> — a tight close-up of the label. This is critical for
            preserving the gold "NOVERA" wordmark. Single-angle products produce
            hallucinated label text; a label close-up almost always fixes it.
          </li>
        </UL>
        <P>
          Any other roles — lifestyle, packaging — are for your own organisation. The AI
          defaults to hero, 3/4, and label when building composites. Lifestyle and packaging
          shots are picked up when you manually select them in the Create Ad or Create Image
          wizards.
        </P>
        <Callout variant="tip">
          If a composite or video ad renders with a garbled label — scrambled text, a missing
          logo, wrong typeface — the fix is almost always to link a label close-up with the
          role <Tag variant="default">label_detail</Tag>. This single addition resolves the
          issue in the vast majority of cases.
        </Callout>

        <H3>Creating an ad from a product</H3>
        <P>
          Each product card has a Create Ad shortcut. Clicking it opens the Create Ad wizard
          pre-filled with that product's description and images — you skip the "describe your
          product" step entirely and go straight to choosing your ad format and scene direction.
        </P>

        <H3>Deleting</H3>
        <P>
          Deleting a product removes the product record and its image-role links. It does not
          delete the underlying images. Those stay in your Asset Library under the product's
          folder and can be re-linked to another product whenever you need them.
        </P>
      </HowToUse>

      <BehindScenes>
        <H3>When you scrape a product page</H3>
        <Flow
          steps={[
            'You paste your product page URL and click Scrape',
            'The system fetches the page',
            'Gemini reads the page content and pulls out name, description, benefits, ingredients, and price',
            'The hero image is downloaded and stored in your Asset Library',
            'A product record is created, linked to that image with the role hero_image',
            'In the background, Gemini describes and tags the image so it becomes searchable in the library',
          ]}
        />

        <H3>Why the Products library matters across the app</H3>
        <UL>
          <li>
            <strong>Create Ad</strong> — seeds the script with the product's name, benefits,
            and description. The AI knows what you're selling without you retyping it. A
            Novera Smooth Kit ad gets the right ingredient story; a Radiance ad gets the
            right sensorial language.
          </li>
          <li>
            <strong>Create Image, Composite tab</strong> — pulls your hero, 3/4, and label
            images automatically so labels stay accurate and the product looks like itself
            in the final composite.
          </li>
          <li>
            <strong>Saved Context</strong> — product images organise themselves into a folder
            named after the product. Your Novera Body Oil shots live together, separate from
            your Hair Revival Kit shots.
          </li>
        </UL>

        <H3>What the AI sees about each product</H3>
        <KVTable
          rows={[
            {
              key: 'Name',
              value: 'Mentioned in every scene prompt for that product',
            },
            {
              key: 'Description',
              value:
                'Sets the tone — "nourishing and sumptuous" versus "clinical and results-led" versus "sensorial and botanical"',
            },
            {
              key: 'Benefits',
              value:
                'The AI weaves these into hooks and CTAs — "nourish, hydrate, smooth" for Novera Body Oil; "strengthen, stimulate, restore" for the Hair Revival Kit',
            },
            {
              key: 'Ingredients',
              value:
                'Useful for ingredient-led hooks — "made with rosehip and primrose" or "patchouli and sweet almond oil"',
            },
            {
              key: 'Brand voice',
              value: 'Overrides the default voice for that product — "ethereal and sensorial" versus "direct and results-focused"',
            },
            {
              key: 'Target audience',
              value: 'Shapes who the AI imagines speaking to — informs scene direction and avatar casting',
            },
            {
              key: 'Usage tips',
              value:
                'Informs how the avatar or voiceover describes the product in use — the warm-oil-in-palm moment for Novera Body Oil, the press-and-release for Radiance',
            },
          ]}
        />

        <Callout variant="info">
          Richer product records produce noticeably better ads. Don't skip ingredients or
          benefits — they are what let the AI write copy in your brand voice rather than
          generic beauty-brand language.
        </Callout>
      </BehindScenes>
    </>
  )
}
